import type { LessonDepth } from '@/types/api';

/**
 * Shared option sets for lesson depth + flow, used by both the onboarding
 * wizard (grid tiles) and Settings → Learning style. Single source of truth so
 * the two surfaces never drift.
 */

/**
 * Depth never changes WHICH concepts a lesson covers (every concept in the
 * material is taught); it sets how much each one gets. The per-concept
 * amounts mirror the backend settings (`lesson_text_words_per_concept_*`,
 * `lesson_quiz_per_concept_*`, `lesson_flashcards_per_concept_*`).
 */
export interface DepthOption {
  value: LessonDepth;
  label: string;
  sub: string;
  wordsPerConcept: number;
  questionsPerConcept: number;
}

export const DEPTH_OPTIONS: DepthOption[] = [
  {
    value: 'brief',
    label: 'Brief',
    sub: 'Every concept, in short',
    wordsPerConcept: 150,
    questionsPerConcept: 1,
  },
  {
    value: 'standard',
    label: 'Standard',
    sub: 'Every concept, explained (default)',
    wordsPerConcept: 300,
    questionsPerConcept: 2,
  },
  {
    value: 'deep',
    label: 'Deep',
    sub: 'Every concept in full, more practice',
    wordsPerConcept: 500,
    questionsPerConcept: 3,
  },
];

export const DEFAULT_DEPTH: LessonDepth = 'standard';

/** Built-in lesson flows (which artifacts get generated). Mirrors the
 *  `FLOWS` presets in FlowSelector and `DEFAULT_FLOWS` in the backend. Every
 *  flow starts with the mindmap: it is the lesson plan, shown first. */
export type FlowPreset = 'quick' | 'core' | 'full';

export interface FlowOption {
  value: FlowPreset;
  label: string;
  sub: string;
}

export const FLOW_PRESETS: FlowOption[] = [
  {
    value: 'quick',
    label: 'Quick',
    sub: 'Mindmap & key highlights',
  },
  {
    value: 'core',
    label: 'Core',
    sub: 'Mindmap, text & highlights (default)',
  },
  {
    value: 'full',
    label: 'Full',
    sub: 'Mindmap, text, quiz, glossary & highlights',
  },
];

export const DEFAULT_FLOW: FlowPreset = 'core';
